import React, { useState } from 'react';
import moment from 'moment-timezone';
import classNames from 'classnames';

// redux
import { useDispatch, useSelector } from 'react-redux';
import { deleteDailyQuote } from 'redux/dailyQuotes-service/actions';
import { Store } from 'redux/root';

// components
import { AddQuoteModal } from '../AddQuoteModal';
import { MenuItem, IconButton, Tooltip } from '@material-ui/core';

// icons
import { ReactComponent as EditSvg } from 'assets/image/icons/edit.svg';
import { ReactComponent as DeleteSvg } from 'assets/image/trash-light.svg';
import { ReactComponent as ThreeDotsSvg } from 'assets/image/threeDots.svg';

// types & constants
import { IQuoteExtendedResponse } from '@joc/api-gateway/lib/api-client';
import {
	createEmptyLocalizedMap,
	LocalizedStringsMap,
	SUPPORTED_LANGUAGES,
	SupportedLanguageKey,
} from 'core/constants/locales';
import { useLocales } from 'hooks/locales';

// styles
import styles from './DailyQuotes.module.scss';
import StyledMenu from '../../../Menu';

export const DailyQuotesTable = () => {
	const dispatch = useDispatch();
	const { languageKeyByLocaleId } = useLocales();

	const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
	const [quotesToEdit, setQuotesToEdit] = useState<IQuoteExtendedResponse | undefined>(undefined);
	const [openItemId, setOpenItemId] = useState<number>(0);
	const [selectedLangByQuoteId, setSelectedLangByQuoteId] = useState<Record<number, SupportedLanguageKey>>({});
	const dailyQuotesRecords = useSelector((store: Store) => store.quotesRedux.quotesRecords);

	const getQuoteTranslations = (quote: IQuoteExtendedResponse): LocalizedStringsMap => {
		const map = createEmptyLocalizedMap();
		if (quote.message) {
			map.en = quote.message;
		}
		quote.quoteLocalized?.forEach(item => {
			const langKey = languageKeyByLocaleId[item.localeId];
			if (langKey && item.messageLocalized?.trim()) {
				map[langKey] = item.messageLocalized;
			}
		});
		if (!map.en.trim() && quote.quoteLocalized?.[0]?.messageLocalized) {
			map.en = quote.quoteLocalized[0].messageLocalized;
		}
		return map;
	};

	const handleSelectRowLanguage = (
		event: React.MouseEvent<HTMLButtonElement>,
		quoteId: number,
		langKey: SupportedLanguageKey
	) => {
		event.stopPropagation();
		setSelectedLangByQuoteId(prev => ({
			...prev,
			[quoteId]: langKey,
		}));
	};

	const handleActionsButtonClose = () => {
		setAnchorEl(null);
		setOpenItemId(0);
	};

	const handleEditQuote = (quote: IQuoteExtendedResponse) => {
		handleActionsButtonClose();
		setQuotesToEdit(quote);
	};

	const handleDeleteQuote = (quote: IQuoteExtendedResponse) => {
		handleActionsButtonClose();
		dispatch(deleteDailyQuote(quote));
	};

	const closeEditQuote = () => {
		setQuotesToEdit(undefined);
	};

	const handleActionsButtonClick = (event: React.MouseEvent<HTMLElement>, quote: IQuoteExtendedResponse) => {
		event.stopPropagation();
		setAnchorEl(event.currentTarget);
		setOpenItemId(quote.id);
	};

	return (
		<>
			<table className={styles.quotesTable}>
				<thead>
					<tr>
						<th className={styles.quotesTableDate}>Date</th>
						<th className={styles.quotesTableName}>Daily Quotes</th>
						<th className={styles.quotesTableLanguages}>Languages</th>
						<th aria-label="Actions" />
					</tr>
				</thead>
				<tbody>
					{dailyQuotesRecords.length
						? dailyQuotesRecords
								.sort((a, b) => a.startDate.getTime() - b.startDate.getTime())
								.map((quote, index) => {
									const translations = getQuoteTranslations(quote);
									const activeLang = selectedLangByQuoteId[quote.id] || 'en';
									const activeText = translations[activeLang]?.trim();
									const displayMessage = activeText || translations.en || '';
									const isFallback = activeLang !== 'en' && !activeText;
									const isRtl = activeLang === 'he' && Boolean(activeText);

									return (
										<tr
											key={`${quote.startDate.getTime()}${index}`}
											className={styles.quotesTableRow}
											onClick={() => handleEditQuote(quote)}
										>
											<td
												className={styles.quotesTableDate}
												title={moment(quote.startDate).format('DD/MM/YYYY')}
											>
												<span>{moment(quote.startDate).format('DD/MM/YYYY')}</span>
											</td>
											<td className={styles.quotesTableName} title={displayMessage}>
												<div
													className={classNames(styles.quoteTextWrapper, {
														[styles.rtlText]: isRtl,
													})}
													dir={isRtl ? 'rtl' : 'ltr'}
												>
													<span>{displayMessage}</span>
													{isFallback && (
														<em className={styles.fallbackBadge}>Fallback: EN</em>
													)}
												</div>
											</td>
											<td
												className={styles.quotesTableLanguages}
												onClick={event => event.stopPropagation()}
											>
												<div className={styles.langPills}>
													{SUPPORTED_LANGUAGES.map(lang => {
														const value = translations[lang.key]?.trim();
														const hasValue = Boolean(value);
														const isActive = activeLang === lang.key;
														const tooltipText = hasValue
															? `${lang.label}: ${value}`
															: `${lang.label}: Not configured (Fallback: English)`;

														return (
															<Tooltip key={lang.key} title={tooltipText} arrow>
																<button
																	type="button"
																	className={classNames(styles.langPill, {
																		[styles.langPillFilled]: hasValue,
																		[styles.langPillEmpty]: !hasValue,
																		[styles.langPillActive]: isActive,
																	})}
																	onClick={event =>
																		handleSelectRowLanguage(
																			event,
																			quote.id,
																			lang.key
																		)
																	}
																>
																	{lang.key.toUpperCase()}
																	{hasValue && <span className={styles.langDot} />}
																</button>
															</Tooltip>
														);
													})}
												</div>
											</td>
											<td align="center" onClick={event => event.stopPropagation()}>
												<IconButton
													aria-label="actions"
													aria-controls={openItemId === quote.id ? 'long-menu' : undefined}
													onClick={event => handleActionsButtonClick(event, quote)}
												>
													<ThreeDotsSvg />
												</IconButton>
												<StyledMenu
													open={openItemId === quote.id}
													anchorEl={anchorEl}
													onClose={handleActionsButtonClose}
												>
													<MenuItem
														className={styles.menuItem}
														onClick={() => handleEditQuote(quote)}
													>
														<div className={styles.actionsContainer}>
															<EditSvg
																style={{ width: '16px', height: '16px', margin: 4 }}
																title="Edit quote"
															/>
															<span className={styles.actionsText}>Edit</span>
														</div>
													</MenuItem>
													<MenuItem
														className={styles.menuItem}
														onClick={() => handleDeleteQuote(quote)}
													>
														<div className={styles.actionsContainer}>
															<DeleteSvg
																style={{ width: '16px', height: '16px', margin: 4 }}
																title="Delete quote"
															/>
															<span className={styles.actionsText}>Delete</span>
														</div>
													</MenuItem>
												</StyledMenu>
											</td>
										</tr>
									);
								})
						: null}
				</tbody>
			</table>
			{(quotesToEdit && (
				<AddQuoteModal isModalOpen={Boolean(quotesToEdit)} onClose={closeEditQuote} quote={quotesToEdit} />
			)) || <></>}
		</>
	);
};
